import { PlatformComponent } from "../services/platform/types";
import { GenericAssessment } from "../types";
import { AssessmentComponent } from "./componentTypes";
import { ComponentConditionKey, ComponentConditionValues, GenericCondition } from "./conditions";

export class TypedAssessment<TConditionType extends string> implements GenericAssessment<ComponentConditionValues> {
	AvailableConditions: ComponentConditionValues[];
	ComponentKey: AssessmentComponent;
	ComponentName: string;
	private _condition: ComponentConditionValues | undefined;
	AssessmentId: string;
	private reverseOrder = false;
	onConditionUpdate: (newCondition: TypedAssessment<TConditionType>) => void = () => {};
	constructor(
		componentKey: AssessmentComponent,
		assessmentId: string,
		component: PlatformComponent,
		conditionOrder: TConditionType[],
		reverseOrder: boolean = false,
		initialCondition: TConditionType | undefined = undefined,
	) {
		this.ComponentName = component.name;
		this.ComponentKey = componentKey;
		this.AssessmentId = assessmentId;
		this.reverseOrder = reverseOrder;
		const conditions = this.mapConditionsToArray(component);
		this.AvailableConditions = this.sortConditions(conditionOrder, conditions);
		if (initialCondition) this._condition = this.AvailableConditions.find((c) => c.Name === initialCondition);
	}
	get Condition() {
		return this._condition;
	}
	set Condition(condition: ComponentConditionValues | undefined) {
		this._condition = condition;
		this.onConditionUpdate(this);
	}
	public SetDefaultCondition() {
		this.Condition = this.AvailableConditions[0];
		return this.Condition;
	}
	public GetDefaultCondition() {
		return this.AvailableConditions[0];
	}
	public SetConditionByKey(condition: ComponentConditionKey) {
		this.Condition = this.AvailableConditions.find((c) => c.Name === condition);
	}

	public SetBooleanCondition(working: boolean) {
		this.Condition = this.GetConditionByBoolean(working);
	}
	public GetSelectedBooleanCondition(): boolean | undefined {
		if (this.Condition) {
			this.GetBooleanCondition(this.Condition);
		}
		return undefined;
	}
	public GetConditionByBoolean(bestCondition: boolean): ComponentConditionValues {
		return bestCondition
			? this.AvailableConditions[0]!
			: this.AvailableConditions[this.AvailableConditions.length - 1]!;
	}
	public GetBooleanCondition(condition: ComponentConditionValues): boolean {
		return this.AvailableConditions.indexOf(condition) === 0;
	}
	public GetSelectedGenericCondition(): GenericCondition | undefined {
		return this.Condition ? this.GetGenericCondition(this.Condition) : undefined;
	}
	public GetGenericCondition(condition: ComponentConditionValues): GenericCondition {
		const index = this.AvailableConditions.indexOf(condition);
		if (index === 0) return GenericCondition.Optimal;
		else if (index === this.AvailableConditions.length - 1) return GenericCondition.Worst;
		else return GenericCondition.Average;
	}
	protected mapConditionsToArray(component: PlatformComponent): ComponentConditionValues[] {
		const conditionsArray: ComponentConditionValues[] = [];
		for (const conditionName in component.conditions) {
			const conditionId = component.conditions[conditionName];
			if (!conditionId) continue;
			const condition: ComponentConditionValues = {
				Name: conditionName as unknown as ComponentConditionKey,
				Id: conditionId,
			};
			conditionsArray.push(condition);
		}
		return conditionsArray;
	}
	protected sortConditions(conditionsOrder: TConditionType[], conditions: ComponentConditionValues[]) {
		const keys = conditionsOrder;
		function lastIndexIfNotFound(cName: string) {
			const index = keys.indexOf(cName as TConditionType);
			return index < 0 ? keys.length : index;
		}
		if (this.reverseOrder)
			return conditions.sort((c1, c2) => lastIndexIfNotFound(c2.Name) - lastIndexIfNotFound(c1.Name));
		else return conditions.sort((c1, c2) => lastIndexIfNotFound(c1.Name) - lastIndexIfNotFound(c2.Name));
	}
}
